import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Panel } from "../components/common";
import { scoreMeta, chgColor, pct, fmt, classColors, classLabel } from "../lib/format";
import { ListOrdered } from "lucide-react";

const SORTS = [
  ["smart_quant_score", "Top Assets"], ["momentum", "Best Momentum"],
  ["fundamental", "Best Fundamentals"], ["macro", "Best Macro"],
  ["seasonal", "Best Seasonality"], ["technical", "Best Technical"],
  ["volatility", "Best Risk-Adjusted"], ["liquidity", "Best Liquidity"],
];
const CLASSES = ["all", "equity", "etf_equity", "commodity", "fixed_income", "forex", "crypto", "volatility"];

export default function Rankings() {
  const [rows, setRows] = useState([]);
  const [sort, setSort] = useState("smart_quant_score");
  const [cls, setCls] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/rankings", { params: { sort, asset_class: cls, min_score: minScore } })
      .then((r) => setRows(r.data)).catch(() => {});
  }, [sort, cls, minScore]);

  return (
    <div className="space-y-5" data-testid="rankings-page">
      <div>
        <div className="eyebrow">multi-factor screener</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Quant Rankings</h1>
      </div>

      <div className="flex flex-wrap gap-2">
        {SORTS.map(([k, l]) => (
          <button key={k} data-testid={`rankings-sort-${k}`} onClick={() => setSort(k)}
            className={`text-xs font-mono px-3 py-1.5 rounded-md border transition-colors ${sort === k ? "bg-[#00E5FF12] text-[#00E5FF] border-[#00E5FF44]" : "text-slate-400 border-[#334155] hover:bg-[#141C2E]"}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-1.5">
          {CLASSES.map((c) => (
            <button key={c} data-testid={`rankings-filter-${c}`} onClick={() => setCls(c)}
              className={`text-[11px] font-mono px-2.5 py-1 rounded border transition-colors ${cls === c ? "text-white border-[#00E5FF66] bg-[#00E5FF12]" : "text-slate-400 border-[#1E293B] hover:bg-[#141C2E]"}`}>
              {c === "all" ? "All Classes" : classLabel[c]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <span className="eyebrow">min score</span>
          <input data-testid="rankings-min-score" type="range" min="0" max="80" value={minScore}
            onChange={(e) => setMinScore(Number(e.target.value))} className="accent-[#00E5FF] w-32" />
          <span className="font-mono text-xs w-6">{minScore}</span>
        </div>
      </div>

      <Panel title={`${SORTS.find((s) => s[0] === sort)[1]} — ${rows.length} assets`} icon={<ListOrdered size={13} />}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="rankings-table">
            <thead>
              <tr className="eyebrow text-left border-b border-[#1E293B]">
                <th className="py-2 pr-2">#</th><th className="pr-2">Asset</th><th className="pr-2">Class</th>
                <th className="pr-2 text-right">Price</th><th className="pr-2 text-right">Chg</th>
                <th className="pr-2 text-right">Mom6M</th><th className="pr-2 text-right">Vol</th>
                <th className="pr-2 text-center">Trend</th><th className="pr-2 text-right">Score</th><th className="text-right">Signal</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const m = scoreMeta(r.smart_quant_score);
                return (
                  <tr key={r.symbol} data-testid={`rankings-row-${r.symbol}`} onClick={() => nav(`/asset/${r.symbol}`)}
                    className="hover-row cursor-pointer border-b border-[#141C2E]">
                    <td className="py-2 pr-2 font-mono text-slate-500 tnum">{i + 1}</td>
                    <td className="pr-2"><span className="font-mono font-bold">{r.symbol}</span><span className="hidden md:inline text-slate-500 text-xs ml-2">{r.name}</span></td>
                    <td className="pr-2"><span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: `${classColors[r.asset_class]}18`, color: classColors[r.asset_class] }}>{classLabel[r.asset_class]}</span></td>
                    <td className="pr-2 text-right font-mono tnum">{fmt(r.price)}</td>
                    <td className="pr-2 text-right font-mono tnum" style={{ color: chgColor(r.change_1d) }}>{pct(r.change_1d)}</td>
                    <td className="pr-2 text-right font-mono tnum" style={{ color: chgColor(r.mom_6m) }}>{pct(r.mom_6m)}</td>
                    <td className="pr-2 text-right font-mono tnum text-slate-400">{fmt(r.realized_vol, 1)}%</td>
                    <td className="pr-2 text-center font-mono text-xs" style={{ color: r.trend_long.direction > 0 ? "#10B981" : r.trend_long.direction < 0 ? "#EF4444" : "#F59E0B" }}>{r.trend_long.state}</td>
                    <td className="pr-2 text-right font-mono font-extrabold tnum" style={{ color: m.color }}>{Math.round(r.smart_quant_score)}</td>
                    <td className="text-right"><span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ color: m.color, background: `${m.color}18` }}>{r.classification}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
